import React, { useEffect, useState, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import { api } from "../../services/api/api";

// Opções de situação/progresso (lista fixa) - definida antes do componente
const progressOptions = [
  { id: 0, name: "Sem prazo", color: "#9E9E9E" },
  { id: 1, name: "Atrasado", color: "#F44336" },
  { id: 2, name: "Próximo do prazo", color: "#FFC107" },
  { id: 3, name: "Em dia", color: "#4CAF50" },
];

/**
 * Componente de filtros avançados para a listagem de projetos
 * Inspirado no PlantAdvancedFiltersCollapsible
 */
const ProjectAdvancedFilters = ({ onFiltersChange, initialFilters = {} }) => {
  const [expanded, setExpanded] = useState(false);
  const isFirstRender = useRef(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // Estados para os dados das opções de filtro
  const [clients, setClients] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [stages, setStages] = useState([]);
  const [states, setStates] = useState([]);
  
  // Estados para os filtros selecionados - inicializados com initialFilters
  const [selectedClients, setSelectedClients] = useState(() => {
    if (!initialFilters.clientIds || !clients.length) return [];
    const ids = initialFilters.clientIds.split(',').map(Number);
    return clients.filter(c => ids.includes(c.id));
  });
  
  const [selectedEngineers, setSelectedEngineers] = useState(() => {
    if (!initialFilters.engineerIds || !engineers.length) return [];
    const ids = initialFilters.engineerIds.split(',').map(Number);
    return engineers.filter(e => ids.includes(e.id));
  });
  
  const [selectedStages, setSelectedStages] = useState(() => {
    if (!initialFilters.stageIds || !stages.length) return [];
    const ids = initialFilters.stageIds.split(',').map(Number);
    return stages.filter(s => ids.includes(s.id));
  });
  
  const [selectedStates, setSelectedStates] = useState(() => {
    if (!initialFilters.stateIds || !states.length) return [];
    const stateIds = initialFilters.stateIds.split(',');
    return states.filter(s => stateIds.includes(s.id));
  });
  
  const [selectedProgress, setSelectedProgress] = useState(() => {
    if (!initialFilters.progressIds) return [];
    const ids = initialFilters.progressIds.split(',').map(Number);
    return progressOptions.filter(p => ids.includes(p.id));
  });
  
  const [commentSearch, setCommentSearch] = useState(initialFilters.commentSearch || "");
  
  // Carrega as opções de filtro da API
  useEffect(() => {
    loadFilterOptions();
  }, []);
  
  // Efeito para sincronizar filtros selecionados quando os dados da API chegam
  useEffect(() => {
    if (initialFilters.clientIds && clients.length > 0) {
      const ids = initialFilters.clientIds.split(',').map(Number);
      setSelectedClients(clients.filter(c => ids.includes(c.id)));
    }
  }, [clients, initialFilters.clientIds]);
  
  useEffect(() => {
    if (initialFilters.engineerIds && engineers.length > 0) {
      const ids = initialFilters.engineerIds.split(',').map(Number);
      setSelectedEngineers(engineers.filter(e => ids.includes(e.id)));
    }
  }, [engineers, initialFilters.engineerIds]);
  
  useEffect(() => {
    if (initialFilters.stageIds && stages.length > 0) {
      const ids = initialFilters.stageIds.split(',').map(Number);
      setSelectedStages(stages.filter(s => ids.includes(s.id)));
    }
  }, [stages, initialFilters.stageIds]);
  
  useEffect(() => {
    if (initialFilters.stateIds && states.length > 0) {
      const stateIds = initialFilters.stateIds.split(',');
      setSelectedStates(states.filter(s => stateIds.includes(s.id)));
    }
  }, [states, initialFilters.stateIds]);
  
  useEffect(() => {
    if (initialFilters.progressIds) {
      const ids = initialFilters.progressIds.split(',').map(Number);
      setSelectedProgress(progressOptions.filter(p => ids.includes(p.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters.progressIds]);
  
  // Efeito que notifica mudanças de filtro ao pai (igual ao PlantAdvancedFiltersCollapsible)
  useEffect(() => {
    // Não notifica no primeiro render para permitir que os dados da API sejam carregados primeiro
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Não notifica mudanças até que os dados da API tenham sido carregados
    if (!isDataLoaded) {
      return;
    }
    
    const filters = {
      clientIds: selectedClients.length > 0 ? selectedClients.map(c => c.id).join(",") : undefined,
      engineerIds: selectedEngineers.length > 0 ? selectedEngineers.map(e => e.id).join(",") : undefined,
      stageIds: selectedStages.length > 0 ? selectedStages.map(s => s.id).join(",") : undefined,
      stateIds: selectedStates.length > 0 ? selectedStates.map(s => s.id).join(",") : undefined,
      progressIds: selectedProgress.length > 0 ? selectedProgress.map(p => p.id).join(",") : undefined,
      commentSearch: commentSearch.trim() ? commentSearch.trim() : undefined,
    };
    onFiltersChange(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClients, selectedEngineers, selectedStages, selectedStates, selectedProgress, commentSearch, isDataLoaded]);
  
  const loadFilterOptions = async () => {
    try {
      // Carregar integradores (clientes)
      const clientsRes = await api.get("/clients");
      if (clientsRes.data) {
        setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      }
      
      // Carregar engenheiros
      const engineersRes = await api.get("/engineers");
      if (engineersRes.data) {
        setEngineers(Array.isArray(engineersRes.data) ? engineersRes.data : []);
      }
      
      // Carregar etapas
      const stagesRes = await api.get("/stages");
      if (stagesRes.data) {
        setStages(Array.isArray(stagesRes.data) ? stagesRes.data : []);
      }
      
      // Estados brasileiros (lista fixa)
      setStates([
        { id: "AC", name: "Acre" },
        { id: "AL", name: "Alagoas" },
        { id: "AP", name: "Amapá" },
        { id: "AM", name: "Amazonas" },
        { id: "BA", name: "Bahia" },
        { id: "CE", name: "Ceará" },
        { id: "DF", name: "Distrito Federal" },
        { id: "ES", name: "Espírito Santo" },
        { id: "GO", name: "Goiás" },
        { id: "MA", name: "Maranhão" },
        { id: "MT", name: "Mato Grosso" },
        { id: "MS", name: "Mato Grosso do Sul" },
        { id: "MG", name: "Minas Gerais" },
        { id: "PA", name: "Pará" },
        { id: "PB", name: "Paraíba" },
        { id: "PR", name: "Paraná" },
        { id: "PE", name: "Pernambuco" },
        { id: "PI", name: "Piauí" },
        { id: "RJ", name: "Rio de Janeiro" },
        { id: "RN", name: "Rio Grande do Norte" },
        { id: "RS", name: "Rio Grande do Sul" },
        { id: "RO", name: "Rondônia" },
        { id: "RR", name: "Roraima" },
        { id: "SC", name: "Santa Catarina" },
        { id: "SP", name: "São Paulo" },
        { id: "SE", name: "Sergipe" },
        { id: "TO", name: "Tocantins" },
      ]);
      
      // Marca que os dados foram carregados após um pequeno delay para garantir sincronização
      setTimeout(() => {
        setIsDataLoaded(true);
      }, 100);
    } catch (error) {
      console.error("Erro ao carregar opções de filtro:", error);
      setIsDataLoaded(true);
    }
  };
  
  const clearAllFilters = () => {
    setSelectedClients([]);
    setSelectedEngineers([]);
    setSelectedStages([]);
    setSelectedStates([]);
    setSelectedProgress([]);
    setCommentSearch("");
  };
  
  const hasActiveFilters =
    selectedClients.length > 0 ||
    selectedEngineers.length > 0 ||
    selectedStages.length > 0 ||
    selectedStates.length > 0 ||
    selectedProgress.length > 0 ||
    commentSearch.trim().length > 0;
  
  const activeFiltersChips = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", ml: 2 }}>
      <Typography variant="caption" sx={{ color: "inherit", opacity: 0.8 }}>
        Filtros ativos:
      </Typography>
      {selectedClients.length > 0 && (
        <Chip
          label={`Integrador: ${selectedClients.map(c => c.name).join(', ')}`}
          size="small"
          onDelete={() => setSelectedClients([])}
          sx={{
            color: "inherit",
            borderColor: "currentColor",
            "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.7 },
          }}
          variant="outlined"
        />
      )}
      {selectedEngineers.length > 0 && (
        <Chip
          label={`Engenheiro: ${selectedEngineers.map(e => e.name).join(', ')}`}
          size="small"
          onDelete={() => setSelectedEngineers([])}
          sx={{
            color: "inherit",
            borderColor: "currentColor",
            "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.7 },
          }}
          variant="outlined"
        />
      )}
      {selectedStages.length > 0 && (
        <Chip
          label={`Etapa: ${selectedStages.map(s => s.stageName || s.name).join(', ')}`}
          size="small"
          onDelete={() => setSelectedStages([])}
          sx={{
            color: "inherit",
            borderColor: "currentColor",
            "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.7 },
          }}
          variant="outlined"
        />
      )}
      {selectedStates.length > 0 && (
        <Chip
          label={`Estado: ${selectedStates.map(s => s.id).join(', ')}`}
          size="small"
          onDelete={() => setSelectedStates([])}
          sx={{
            color: "inherit",
            borderColor: "currentColor",
            "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.7 },
          }}
          variant="outlined"
        />
      )}
      {selectedProgress.length > 0 && (
        <Chip
          label={`Situação: ${selectedProgress.map(p => p.name).join(', ')}`}
          size="small"
          onDelete={() => setSelectedProgress([])}
          sx={{
            color: "inherit",
            borderColor: "currentColor",
            "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.7 },
          }}
          variant="outlined"
        />
      )}
      {commentSearch.trim() && (
        <Chip
          label={`Comentário: "${commentSearch}"`}
          size="small"
          onDelete={() => setCommentSearch("")}
          sx={{
            color: "inherit",
            borderColor: "currentColor",
            "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.7 },
          }}
          variant="outlined"
        />
      )}
    </Box>
  );
  
  return (
    <Accordion 
      expanded={expanded} 
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      sx={{ 
        mb: 3,
        borderRadius: "12px !important",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: hasActiveFilters ? "primary.main" : "#FAFAFA",
          color: hasActiveFilters ? "primary.contrastText" : "text.primary",
          borderRadius: hasActiveFilters ? "12px" : "12px",
          minHeight: 56,
          "&:hover": {
            backgroundColor: hasActiveFilters ? "primary.dark" : "#F5F5F5",
          },
          "& .MuiAccordionSummary-content": {
            margin: "12px 0",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
          <FilterListIcon />
          <Typography fontWeight="500">Filtros Avançados</Typography>
          {hasActiveFilters && !expanded && activeFiltersChips}
        </Box>
        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={(e) => {
              e.stopPropagation();
              clearAllFilters();
            }}
            sx={{
              color: hasActiveFilters ? "primary.contrastText" : "primary.main",
              mr: 2,
            }}
          >
            Limpar Filtros
          </Button>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3, backgroundColor: "#FAFAFA" }}>
        <Grid container spacing={3}>
          {/* Filtro por Integrador */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Filtrar por Integrador:
            </Typography>
            <Autocomplete
              multiple
              options={clients}
              getOptionLabel={(option) => option.name}
              value={selectedClients}
              onChange={(e, newValue) => setSelectedClients(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Selecione os integradores" 
                  placeholder="Integradores"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "white",
                    },
                  }}
                />
              )}
            />
          </Grid>
          
          {/* Filtro por Engenheiro */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Filtrar por Engenheiro:
            </Typography>
            <Autocomplete
              multiple
              options={engineers}
              getOptionLabel={(option) => option.name}
              value={selectedEngineers}
              onChange={(e, newValue) => setSelectedEngineers(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Selecione os engenheiros" 
                  placeholder="Engenheiros"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "white",
                    },
                  }}
                />
              )}
            />
          </Grid>
          
          {/* Filtro por Etapa */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Filtrar por Etapa:
            </Typography>
            <Autocomplete
              multiple
              options={stages}
              getOptionLabel={(option) => option.stageName || option.name}
              value={selectedStages}
              onChange={(e, newValue) => setSelectedStages(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Selecione as etapas" 
                  placeholder="Etapas"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "white",
                    },
                  }}
                />
              )}
            />
          </Grid>
          
          {/* Filtro por Estado */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Filtrar por Estado:
            </Typography>
            <Autocomplete
              multiple
              options={states}
              getOptionLabel={(option) => `${option.id} - ${option.name}`}
              value={selectedStates}
              onChange={(e, newValue) => setSelectedStates(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Selecione os estados" 
                  placeholder="Estados"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "white",
                    },
                  }}
                />
              )}
            />
          </Grid>
          
          {/* Busca em Comentários */}
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "primary.main" }}>
              Buscar em Comentários (protocolo, texto específico):
            </Typography>
            <TextField
              fullWidth
              value={commentSearch}
              onChange={(e) => setCommentSearch(e.target.value)}
              placeholder="Ex: Protocolo 12345, texto do comentário..."
              helperText="Busca projetos que contenham este texto em qualquer comentário"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "white",
                },
              }}
            />
          </Grid>
          
          {/* Filtro por Situação/Progresso */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Filtrar por Situação:
            </Typography>
            <Autocomplete
              multiple
              options={progressOptions}
              getOptionLabel={(option) => option.name}
              value={selectedProgress}
              onChange={(e, newValue) => setSelectedProgress(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: option.color,
                      }}
                    />
                    {option.name}
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: option.color,
                          }}
                        />
                        {option.name}
                      </Box>
                    }
                  />
                ))
              }
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Selecione as situações" 
                  placeholder="Situações"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "white",
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProjectAdvancedFilters;
